import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'
import Badge from '@/components/common/Badge'

function WikiDocumentView({ wikiItems, searchQuery, teamId }) {
  const [recentDispatches, setRecentDispatches] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (teamId) {
      fetchRecentDispatches()
    }
  }, [teamId])

  const fetchRecentDispatches = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('dispatches')
        .select('id, title, sent_at')
        .eq('team_id', teamId)
        .order('sent_at', { ascending: false })
        .limit(5)

      if (error) throw error

      setRecentDispatches(data || [])
    } catch (err) {
      console.error('Failed to fetch recent dispatches:', err)
    } finally {
      setLoading(false)
    }
  }

  // Define section order and organize items
  const sectionOrder = ['Tournaments', 'Team Contacts', 'Policies & Rules', 'Logistics', 'Training & Resources', 'Quick Notes']
  
  // Separate parents and children
  // Note: DB uses parent_id, some legacy code used parent_item_id - support both for compatibility
  const parentItems = (wikiItems || []).filter(item => !item.parent_id && !item.parent_item_id)
  const childItems = (wikiItems || []).filter(item => item.parent_id || item.parent_item_id)
  
  // Group by section with parent-child relationships
  const sections = {}
  sectionOrder.forEach(section => {
    const sectionParents = parentItems.filter(item => item.section === section)
    sections[section] = sectionParents.map(parent => ({
      ...parent,
      children: childItems.filter(child => (child.parent_id || child.parent_item_id) === parent.id)
    }))
  })

  // Filter items based on search
  const filterItems = (items) => {
    if (!searchQuery) return items
    const query = searchQuery.toLowerCase()
    return items.filter(item =>
      item.title.toLowerCase().includes(query) ||
      item.content.toLowerCase().includes(query)
    )
  }

  const highlightText = (text) => {
    if (!searchQuery || !text) return text
    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'))
    return parts.map((part, i) =>
      part.toLowerCase() === searchQuery.toLowerCase() ? (
        <mark key={i} className="bg-yellow-200">{part}</mark>
      ) : (
        part
      )
    )
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  // Get section icon
  const getSectionIcon = (section) => {
    const icons = {
      'Tournaments': '🏆',
      'Team Contacts': '📞',
      'Policies & Rules': '📋',
      'Logistics': '📍',
      'Training & Resources': '📚',
      'Quick Notes': '📝'
    }
    return icons[section] || '📝'
  }

  if (wikiItems.length === 0 && recentDispatches.length === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <p className="text-brand-slate">No wiki items or dispatches yet.</p>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Recent Dispatches Section */}
      {recentDispatches.length > 0 && (
        <Card>
          <div className="mb-4 pb-3 border-b-2 border-border">
            <h2 className="font-heading text-2xl font-bold text-blue-700 flex items-center gap-2">
              <span>📨</span>
              <span>Recent Dispatches</span>
            </h2>
            <p className="text-sm text-brand-slate mt-1">
              Last 5 dispatches sent to parents
            </p>
          </div>

          <div className="space-y-2">
            {recentDispatches.map((dispatch) => (
              <div
                key={dispatch.id}
                className="p-3 bg-blue-50 border border-blue-200 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <p className="font-medium text-brand-charcoal">
                    {dispatch.title || 'Untitled Dispatch'}
                  </p>
                  <span className="text-sm text-brand-slate">
                    {formatDate(dispatch.sent_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Structured Sections */}
      {sectionOrder.map(section => {
        const sectionItems = sections[section] || []
        
        if (sectionItems.length === 0) return null

        const sectionIcon = getSectionIcon(section)

        return (
          <Card key={section}>
            <div className="mb-4 pb-3 border-b-2 border-border">
              <h2 className="font-heading text-2xl font-bold text-green-700 flex items-center gap-2">
                <span>{sectionIcon}</span>
                <span>{section}</span>
              </h2>
            </div>

            <div className="space-y-6">
              {sectionItems.map((parent) => (
                <div key={parent.id}>
                  {/* Parent Item */}
                  <div className="pb-2">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3 className="font-medium text-lg text-brand-charcoal">
                        {highlightText(parent.title)}
                      </h3>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {parent.is_pinned && (
                          <Badge variant="warning">Pinned</Badge>
                        )}
                        {parent.source === 'ai_extracted' && (
                          <Badge variant="secondary">AI</Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-brand-slate">
                      {parent.type === 'link' ? (
                        <a
                          href={parent.content}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-green hover:underline break-all"
                        >
                          {highlightText(parent.content)}
                        </a>
                      ) : (
                        <p className="whitespace-pre-wrap break-words">
                          {highlightText(parent.content)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Child Items (indented) */}
                  {parent.children && parent.children.length > 0 && (
                    <div className="ml-6 mt-3 space-y-3 border-l-2 border-gray-200 pl-4">
                      {parent.children.map((child) => (
                        <div key={child.id} className="pb-2">
                          <div className="flex items-start justify-between gap-4 mb-1">
                            <h4 className="font-medium text-brand-charcoal">
                              {highlightText(child.title)}
                            </h4>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {child.is_pinned && (
                                <Badge variant="warning">Pinned</Badge>
                              )}
                              {child.source === 'ai_extracted' && (
                                <Badge variant="secondary">AI</Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-sm text-brand-slate">
                            {child.type === 'link' ? (
                              <a
                                href={child.content}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-brand-green hover:underline break-all"
                              >
                                {highlightText(child.content)}
                              </a>
                            ) : (
                              <p className="whitespace-pre-wrap break-words">
                                {highlightText(child.content)}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )
      })}
    </div>
  )
}

export default WikiDocumentView
